import ReorderTree from '../components/ReorderTree';
import api from '../api';
import {
    Avatar, Box, Button, Chip, Divider, IconButton,
    Input, Sheet, Stack, Tooltip, Typography,
} from "@mui/joy";
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import AddRoundedIcon                    from "@mui/icons-material/AddRounded";
import PendingActionsRoundedIcon         from "@mui/icons-material/PendingActionsRounded";
import PlayCircleRoundedIcon             from "@mui/icons-material/PlayCircleRounded";
import HourglassEmptyRoundedIcon         from "@mui/icons-material/HourglassEmptyRounded";
import CheckCircleRoundedIcon            from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon                 from "@mui/icons-material/CancelRounded";
import CalendarMonthRoundedIcon          from "@mui/icons-material/CalendarMonthRounded";
import ChatBubbleRoundedIcon             from "@mui/icons-material/ChatBubbleRounded";
import ChevronLeftRoundedIcon            from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon           from "@mui/icons-material/ChevronRightRounded";
import TuneRoundedIcon                   from "@mui/icons-material/TuneRounded";
import SearchRoundedIcon                 from "@mui/icons-material/SearchRounded";
import ViewKanbanRoundedIcon             from "@mui/icons-material/ViewKanbanRounded";
import CircleRoundedIcon                 from "@mui/icons-material/CircleRounded";
import StarRoundedIcon                   from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon             from "@mui/icons-material/StarBorderRounded";
import CheckCircleOutlineRoundedIcon     from "@mui/icons-material/CheckCircleOutlineRounded";


// ─── Config ────────────────────────────────────────────────────────────────────
const MONTHS_SHORT = ["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];

const COLUMNS = [
    { id: "new",         label: "Новые",        color: "#6366f1", icon: <PendingActionsRoundedIcon sx={{ fontSize: 13 }} /> },
    { id: "in_progress", label: "В работе",      color: "#f59e0b", icon: <PlayCircleRoundedIcon    sx={{ fontSize: 13 }} /> },
    { id: "review",      label: "На проверке",   color: "#0ea5e9", icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 13 }} /> },
    { id: "done",        label: "Завершено",     color: "#10b981", icon: <CheckCircleRoundedIcon   sx={{ fontSize: 13 }} /> },
    { id: "blocked",     label: "Заблокировано", color: "#ef4444", icon: <CancelRoundedIcon        sx={{ fontSize: 13 }} /> },
];

const PRIORITY_CONFIG = {
    low:      { label: "Низкий",    color: "#10b981" },
    medium:   { label: "Средний",   color: "#f59e0b" },
    high:     { label: "Высокий",   color: "#ef4444" },
    critical: { label: "Критичный", color: "#dc2626" },
};

const MOCK_PROJECTS = [
    { id: "all",            name: "Все проекты",    color: "#6366f1", tasks: { total: 12, done: 3, active: 6 }, starred: false, isAll: true },
    { id: "crm-backend",    name: "CRM Backend",    color: "#f59e0b", tasks: { total: 4,  done: 1, active: 2 }, starred: true  },
    { id: "mobile-app",     name: "Mobile App",     color: "#10b981", tasks: { total: 2,  done: 0, active: 2 }, starred: true  },
    { id: "admin-panel",    name: "Admin Panel",    color: "#0ea5e9", tasks: { total: 3,  done: 1, active: 1 }, starred: false },
    { id: "infrastructure", name: "Infrastructure", color: "#ef4444", tasks: { total: 2,  done: 1, active: 1 }, starred: false },
    { id: "design-system",  name: "Design System",  color: "#8b5cf6", tasks: { total: 1,  done: 1, active: 0 }, starred: false },
];

const INITIAL_TASKS = [
    { id: 101, title: "Разработка API авторизации",      project: "crm-backend",    status: "in_progress", priority: "high",     assignee: { name: "Алексей П.", avatar: "АП" }, due_date: "2026-03-15", comments: 5,  tags: ["backend"] },
    { id: 102, title: "Дизайн онбординга",               project: "mobile-app",     status: "new",         priority: "medium",   assignee: { name: "Мария С.",   avatar: "МС" }, due_date: "2026-03-18", comments: 2,  tags: ["design"] },
    { id: 103, title: "Unit-тесты модуля оплаты",        project: "crm-backend",    status: "new",         priority: "medium",   assignee: { name: "Дмитрий И.", avatar: "ДИ" }, due_date: "2026-03-20", comments: 0,  tags: ["testing"] },
    { id: 201, title: "Рефакторинг таблицы",             project: "admin-panel",    status: "in_progress", priority: "high",     assignee: { name: "Кирилл Р.",  avatar: "КР" }, due_date: "2026-03-05", comments: 8,  tags: ["frontend"], overdue: true },
    { id: 202, title: "Настройка CI/CD",                 project: "infrastructure", status: "review",      priority: "critical", assignee: { name: "Олег С.",    avatar: "ОС" }, due_date: "2026-03-10", comments: 3,  tags: ["devops"] },
    { id: 203, title: "Интеграция с платёжной системой", project: "crm-backend",    status: "blocked",     priority: "critical", assignee: { name: "Алексей П.", avatar: "АП" }, due_date: "2026-03-03", comments: 12, tags: ["backend"], overdue: true },
    { id: 204, title: "Push-уведомления",                project: "mobile-app",     status: "review",      priority: "high",     assignee: { name: "Мария С.",   avatar: "МС" }, due_date: "2026-03-22", comments: 4,  tags: ["mobile"] },
    { id: 205, title: "Экспорт отчётов в PDF",           project: "admin-panel",    status: "new",         priority: "low",      assignee: { name: "Кирилл Р.",  avatar: "КР" }, due_date: "2026-03-25", comments: 1,  tags: ["reports"] },
    { id: 206, title: "Токены дизайн-системы",           project: "design-system",  status: "done",        priority: "medium",   assignee: { name: "Мария С.",   avatar: "МС" }, due_date: "2026-03-01", comments: 6,  tags: ["design"] },
    { id: 207, title: "Мониторинг серверов",             project: "infrastructure", status: "in_progress", priority: "high",     assignee: { name: "Олег С.",    avatar: "ОС" }, due_date: "2026-03-12", comments: 2,  tags: ["devops"] },
    { id: 208, title: "Восстановление пароля",           project: "crm-backend",    status: "done",        priority: "medium",   assignee: { name: "Дмитрий И.", avatar: "ДИ" }, due_date: "2026-02-28", comments: 3,  tags: ["auth"] },
    { id: 209, title: "Фильтрация в списке задач",       project: "admin-panel",    status: "done",        priority: "low",      assignee: { name: "Кирилл Р.",  avatar: "КР" }, due_date: "2026-02-25", comments: 0,  tags: ["frontend"] },
];


// ─── Utils ─────────────────────────────────────────────────────────────────────
function parseDateParts(dateStr) {
    if (!dateStr) return { day: "—", monthIdx: 0 };
    const parts = dateStr.includes("-") ? dateStr.split("-") : dateStr.split(".");
    if (dateStr.includes("-")) return { day: parts[2], monthIdx: Number(parts[1]) - 1 };
    return { day: parts[0], monthIdx: Number(parts[1]) - 1 };
}


// ─── ProjectItem ───────────────────────────────────────────────────────────────
function ProjectItem({ project, active, onSelect, onToggleStar }) {
    const [hovered, setHovered] = useState(false);
    const pct = project.tasks.total
        ? Math.round((project.tasks.done / project.tasks.total) * 100)
        : 0;

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => onSelect(project.id)}
            sx={{
                position: "relative",
                borderRadius: "10px",
                px: 1.5, py: 1,
                cursor: "pointer",
                transition: "background 0.13s",
                bgcolor: active ? "primary.softBg" : hovered ? "neutral.softBg" : "transparent",
                boxShadow: active ? "inset 0 0 0 1px rgba(99,102,241,0.15)" : "none",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.2}>
                {project.isAll ? (
                    <Box sx={{
                        width: 26, height: 26, borderRadius: "8px",
                        bgcolor: "rgba(99,102,241,0.1)",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                        <ViewKanbanRoundedIcon sx={{ fontSize: 14, color: "#6366f1" }} />
                    </Box>
                ) : (
                    <Box sx={{
                        width: 26, height: 26, borderRadius: "8px",
                        bgcolor: `${project.color}18`,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                        <CircleRoundedIcon sx={{ fontSize: 9, color: project.color }} />
                    </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography sx={{
                            fontWeight: active ? 700 : 500, fontSize: "0.78rem",
                            color: active ? "primary.plainColor" : "text.primary", lineHeight: 1.2,
                        }} noWrap>
                            {project.name}
                        </Typography>
                        <Chip size="sm" variant="soft" sx={{
                            fontSize: "0.6rem", fontWeight: 700, height: 17, minWidth: 22,
                            borderRadius: "5px", flexShrink: 0, ml: 0.5,
                            bgcolor: active ? "rgba(99,102,241,0.12)" : "neutral.softBg",
                            color: active ? "primary.plainColor" : "text.tertiary",
                        }}>
                            {project.tasks.total}
                        </Chip>
                    </Stack>
                    {!project.isAll && (
                        <Box sx={{ mt: 0.5 }}>
                            <Box sx={{ height: 2.5, borderRadius: 4, bgcolor: "neutral.200", overflow: "hidden" }}>
                                <Box sx={{
                                    height: "100%", width: `${pct}%`,
                                    bgcolor: project.color, borderRadius: 4,
                                    transition: "width 0.4s ease",
                                }} />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Stack>

            <AnimatePresence>
                {hovered && !project.isAll && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        transition={{ duration: 0.1 }}
                        style={{ position: "absolute", bottom: 5, right: 6 }}
                    >
                        <IconButton size="sm" variant="plain"
                            onClick={(e) => { e.stopPropagation(); onToggleStar(project.id); }}
                            sx={{
                                width: 18, height: 18, minWidth: 0,
                                color: project.starred ? "#f59e0b" : "text.tertiary",
                                "&:hover": { color: "#f59e0b", bgcolor: "transparent" },
                            }}
                        >
                            {project.starred
                                ? <StarRoundedIcon sx={{ fontSize: 13 }} />
                                : <StarBorderRoundedIcon sx={{ fontSize: 13 }} />
                            }
                        </IconButton>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}


// ─── ProjectsSidebar ───────────────────────────────────────────────────────────
function ProjectsSidebar({ selectedProject, onSelectProject }) {
    const [search, setSearch]     = useState("");
    const [projects, setProjects] = useState(MOCK_PROJECTS);

    const starred  = projects.filter(p => p.starred && !p.isAll);
    const all      = projects.filter(p => !p.starred && !p.isAll);
    const allEntry = projects.find(p => p.isAll);

    const filtered = useMemo(() => {
        if (!search.trim()) return null;
        const q = search.toLowerCase();
        return projects.filter(p => p.name.toLowerCase().includes(q));
    }, [search, projects]);

    function toggleStar(id) {
        setProjects(prev => prev.map(p => p.id === id ? { ...p, starred: !p.starred } : p));
    }

    const activeProject = projects.find(p => p.id === selectedProject);

    return (
        <Sheet variant="soft" sx={{
            mr: 2,
            mb: 1,
            p: 1,
            borderRadius: 20,
            width: 256,
            minWidth: 256,
            overflow: "hidden auto",
            height: "-webkit-fill-available",
            display: "flex", flexDirection: "column",
            transition: "width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s",
            bgcolor: "neutral.50", 
            flexShrink: 0,
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'neutral.100', borderRadius: 15, px: 2, pt: 1, pb: 1, mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                    <Typography level="title-lg" sx={{ fontWeight: 700, color: "text.tertiary" }}>
                        Проекты
                    </Typography>
                    <Tooltip title="Новый проект" size="sm" placement="right">
                        <IconButton size="sm" variant="soft" color="neutral"
                            sx={{ width: 26, height: 26, borderRadius: "8px" }}
                        >
                            <AddRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {/* <Input
                    size="sm"
                    placeholder="Найти проект..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    startDecorator={<SearchRoundedIcon sx={{ fontSize: 14, opacity: 0.45 }} />}
                    sx={{
                        borderRadius: "9px", fontSize: "0.77rem",
                        bgcolor: "background.surface",
                        border: "1px solid transparent",
                        "--Input-focusedHighlight": "rgba(99,102,241,0.25)",
                        "&:focus-within": { borderColor: "rgba(99,102,241,0.35)" },
                    }}
                /> */}
            </Box>

            {/* Active project summary */}
            <AnimatePresence mode="wait">
                {activeProject && !activeProject.isAll && (
                    <motion.div
                        key={activeProject.id}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                    >
                        <Box sx={{
                            mx: 2, mb: 1.5, p: 1.5,
                            borderRadius: "12px",
                            background: `linear-gradient(135deg, ${activeProject.color}12, ${activeProject.color}06)`,
                            border: "1px solid",
                            borderColor: `${activeProject.color}25`,
                        }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Box sx={{
                                    width: 7, height: 7, borderRadius: "50%",
                                    bgcolor: activeProject.color, flexShrink: 0,
                                    boxShadow: `0 0 5px ${activeProject.color}80`,
                                }} />
                                <Typography sx={{ fontSize: "0.73rem", fontWeight: 700, color: "text.primary" }} noWrap>
                                    {activeProject.name}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                {[
                                    { icon: <PendingActionsRoundedIcon sx={{ fontSize: 11 }} />, val: activeProject.tasks.active, label: "активно" },
                                    { icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 11 }} />, val: activeProject.tasks.done, label: "готово" },
                                    { icon: <ViewKanbanRoundedIcon sx={{ fontSize: 11 }} />, val: activeProject.tasks.total, label: "всего" },
                                ].map(({ icon, val, label }) => (
                                    <Stack key={label} alignItems="center" spacing={0.2}>
                                        <Box sx={{ color: "text.tertiary" }}>{icon}</Box>
                                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, lineHeight: 1, color: "text.primary" }}>{val}</Typography>
                                        <Typography sx={{ fontSize: "0.58rem", color: "text.tertiary" }}>{label}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* List */}
            <Box sx={{ flex: 1, overflow: "auto", px: 1, pb: 2 }}>
                {filtered ? (
                    <>
                        <Typography sx={{ px: 1.5, mb: 0.75, color: "text.tertiary", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", fontSize: "0.62rem" }}>
                            Результаты
                        </Typography>
                        {filtered.length === 0
                            ? <Typography sx={{ px: 1.5, py: 2, color: "text.tertiary", fontSize: "0.75rem", textAlign: "center" }}>Не найдено</Typography>
                            : filtered.map(p => (
                                <ProjectItem key={p.id} project={p} active={selectedProject === p.id}
                                    onSelect={onSelectProject} onToggleStar={toggleStar} />
                            ))
                        }
                    </>
                ) : (
                    <>
                        <Box sx={{ mb: 0.5 }}>
                            <ProjectItem project={allEntry} active={selectedProject === "all"}
                                onSelect={onSelectProject} onToggleStar={toggleStar} />
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {starred.length > 0 && (
                            <>
                                <Typography sx={{ px: 1.5, mb: 0.5, color: "text.tertiary", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", fontSize: "0.62rem" }}>
                                    ★ Избранное
                                </Typography>
                                {starred.map(p => (
                                    <ProjectItem key={p.id} project={p} active={selectedProject === p.id}
                                        onSelect={onSelectProject} onToggleStar={toggleStar} />
                                ))}
                                <Divider sx={{ my: 1.5 }} />
                            </>
                        )}

                        <Typography sx={{ px: 1.5, mb: 0.5, color: "text.tertiary", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", fontSize: "0.62rem" }}>
                            Все проекты
                        </Typography>
                        {all.map(p => (
                            <ProjectItem key={p.id} project={p} active={selectedProject === p.id}
                                onSelect={onSelectProject} onToggleStar={toggleStar} />
                        ))}
                    </>
                )}
            </Box>
        </Sheet>
    );
}


// ─── KanbanCard ────────────────────────────────────────────────────────────────
function KanbanCard({ task, columnColor }) {
    const { day, monthIdx } = parseDateParts(task?.due_date);
    const priority = PRIORITY_CONFIG[task?.priority] || PRIORITY_CONFIG["medium"];

    return (
        <Box
            sx={{
                borderRadius: "12px",
                border: "1px solid",
                borderColor: task?.overdue ? "rgba(239,68,68,0.25)" : "divider",
                bgcolor: task?.overdue ? "rgba(239,68,68,0.02)" : "background.surface",
                overflow: "hidden",
                cursor: "grab",
                userSelect: "none",
                transition: "box-shadow 0.15s, transform 0.15s",
                "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.07)", transform: "translateY(-1px)" },
                "&:active": { cursor: "grabbing" },
            }}
        >
            <Box sx={{ height: 2.5, bgcolor: columnColor, opacity: 0.75 }} />

            <Box sx={{ p: 1.5 }}>
                {task?.tags?.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.8 }}>
                        {task.tags.map(tag => (
                            <Typography key={tag} sx={{
                                px: 0.7, py: 0.15, borderRadius: "5px",
                                bgcolor: "neutral.softBg", color: "text.tertiary",
                                fontWeight: 600, fontSize: "0.6rem", letterSpacing: "0.02em",
                            }}>
                                #{tag}
                            </Typography>
                        ))}
                    </Stack>
                )}

                <Typography sx={{ fontWeight: 600, fontSize: "0.78rem", lineHeight: 1.4, color: "text.primary", mb: 1.4 }}>
                    {task.title}
                </Typography>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip title={task.assignee.name} size="sm" placement="top">
                            <Avatar size="sm" variant="soft" color="neutral"
                                sx={{ width: 20, height: 20, fontSize: "0.55rem", fontWeight: 700 }}
                            >
                                {task.assignee.avatar}
                            </Avatar>
                        </Tooltip>
                        <Stack direction="row" alignItems="center" spacing={0.35}>
                            <CalendarMonthRoundedIcon sx={{ fontSize: 11, color: task?.overdue ? "#ef4444" : "text.tertiary" }} />
                            <Typography sx={{
                                fontSize: "0.63rem",
                                color: task?.overdue ? "#ef4444" : "text.tertiary",
                                fontWeight: task?.overdue ? 700 : 400,
                            }}>
                                {day} {MONTHS_SHORT[monthIdx]}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Tooltip title={priority.label} size="sm" placement="top">
                            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: priority.color }} />
                        </Tooltip>
                        {task.comments > 0 && (
                            <Stack direction="row" alignItems="center" spacing={0.3}>
                                <ChatBubbleRoundedIcon sx={{ fontSize: 11, color: "text.tertiary" }} />
                                <Typography sx={{ fontSize: "0.63rem", color: "text.tertiary" }}>{task.comments}</Typography>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
}


// ─── KanbanColumn ──────────────────────────────────────────────────────────────
// ─── KanbanPage ────────────────────────────────────────────────────────────────
export default function KanbanPage() {
    const [tasks, setTasks]                       = useState(INITIAL_TASKS);
    const [selectedProject, setSelectedProject]   = useState("all");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [layoutStatus, setLayoutStatus] = useState('Загрузка расположения…');
    const [ready, setReady] = useState(false), [saving, setSaving] = useState(false);
    const revision = useRef(0), saveLock = useRef(false);
    useEffect(() => {
        let alive = true;
        api.get('/tasks/kanban/layout/').then(({ data }) => {
            if (!alive) return;
            revision.current = data.revision;
            const byId = new Map(INITIAL_TASKS.map(t => [String(t.id), t]));
            const ordered = data.positions.filter(row => byId.has(String(row.id))).map(row => ({ ...byId.get(String(row.id)), status: row.status }));
            const known = new Set(ordered.map(t => t.id));
            setTasks([...ordered, ...INITIAL_TASKS.filter(t => !known.has(t.id))]);
            setReady(true); setLayoutStatus('');
        }).catch(() => { if (alive) setLayoutStatus('Не удалось загрузить расположение. Обновите страницу.'); });
        return () => { alive = false; };
    }, []);
    const arrange = async columns => {
        if (!ready || saveLock.current) return;
        const previous = tasks, visibleIds = new Set(columns.flatMap(col => col.children.map(t => t.id)));
        const next = [...columns.flatMap(col => col.children.map(t => ({ ...t, status: col.id }))), ...tasks.filter(t => !visibleIds.has(t.id))];
        saveLock.current = true; setSaving(true); setTasks(next); setLayoutStatus('Сохраняем порядок…');
        try { const { data } = await api.put('/tasks/kanban/layout/', { positions: next.map(t => ({ id: t.id, status: t.status })), revision: revision.current }); revision.current = data.revision; setLayoutStatus('Порядок сохранён'); }
        catch (e) { setTasks(previous); setLayoutStatus(e.response?.data?.error || 'Не удалось сохранить порядок. Повторите.'); }
        finally { saveLock.current = false; setSaving(false); }
    };

    const filtered = selectedProject === "all"
        ? tasks
        : tasks.filter(t => t.project === selectedProject);

    const tasksByColumn = COLUMNS.reduce((acc, col) => {
        acc[col.id] = filtered.filter(t => t.status === col.id);
        return acc;
    }, {});

    const projectLabel = selectedProject === "all"
        ? "Все проекты"
        : selectedProject.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

    return (
        <Box sx={{ display: "flex", height: "calc(100dvh - 73px)", overflow: "hidden" }}>
            <ProjectsSidebar
                selectedProject={selectedProject}
                onSelectProject={setSelectedProject}
            />

            {/* ── Board ── */}
            <Box sx={{ flex: 1, overflow: "auto", py: 1.3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                    <Box>
                        <Typography level="h4" sx={{ fontWeight: 800, letterSpacing: "-0.3px", lineHeight: 1.1, color: "text.primary" }}>
                            {projectLabel}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "text.tertiary", mt: 0.3 }}>
                            {filtered.length} задач · Канбан-доска
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button size="sm" variant="outlined" color="neutral"
                            startDecorator={<TuneRoundedIcon sx={{ fontSize: 14 }} />}
                            sx={{ borderRadius: "10px", fontSize: "0.73rem", fontWeight: 600, height: 32 }}
                        >
                            Фильтр
                        </Button>
                        <Button size="sm" variant="solid" color="primary"
                            startDecorator={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                            sx={{ borderRadius: "10px", fontSize: "0.73rem", fontWeight: 600, height: 32 }}
                        >
                            Задача
                        </Button>
                    </Stack>
                </Stack>

                {layoutStatus && <Typography role="status" level="body-xs" sx={{ mb: 1, color: 'text.tertiary' }}>{layoutStatus}</Typography>}
                <ReorderTree columns tree={COLUMNS.map(col => ({ ...col, type: 'column', children: (tasksByColumn[col.id] || []).map(t => ({ ...t, name: t.title })) }))}
                    onChange={arrange} disabled={!ready || saving} canRoot={() => false} canContain={parent => parent.type === 'column'}
                    renderHeader={col => <Sheet sx={{ p: 1.5, mb: 1, borderRadius: 12, bgcolor: 'neutral.softBg' }}><Typography level="title-sm" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>{col.icon}{col.label} <Chip size="sm">{col.children.length}</Chip></Typography></Sheet>}
                    render={task => <Box sx={{ flex: 1, minWidth: 0 }}><KanbanCard task={task} columnColor={COLUMNS.find(col => col.id === task.status)?.color}/></Box>}/>

            </Box>
        </Box>
    );
}
